/**
 * Admin API client
 */

const API_URL = process.env.REACT_APP_API_URL || 'https://api.kit8exchange.com';

class AdminAPI {
  constructor() {
    this.token = null;
    this.userId = null;
  }

  setAuth(token, userId) {
    this.token = token;
    this.userId = userId;
  }

  _headers() {
    return {
      'Authorization': `Bearer ${this.token}`,
      'X-User-ID': this.userId,
      'Content-Type': 'application/json',
    };
  }

  async _fetch(method, endpoint, { params, body } = {}) {
    if (!this.token || !this.userId) throw new Error('Not authenticated');

    const url = new URL(`${API_URL}${endpoint}`);
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, v);
      });
    }

    const response = await fetch(url.toString(), {
      method,
      credentials: 'omit',
      headers: this._headers(),
      body: body ? JSON.stringify(body) : undefined,
    });

    if (response.status === 401 || response.status === 403) {
      this.token = null;
      this.userId = null;
      throw new Error('ACCESS_DENIED');
    }

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`API error ${response.status}: ${text}`);
    }

    return response.json();
  }

  async request(endpoint, params = {}) {
    return this._fetch('GET', endpoint, { params });
  }

  // ========== Единый дашборд ==========
  getDashboardData(period = 'day', dailyDays = 7) {
    return this.request('/api/admin/dashboard-data', { period, daily_days: dailyDays });
  }

  // ========== Статистика ==========
  getStats(period = 'day') {
    return this.request('/api/admin/stats', { period });
  }

  getDailyStats(days = 7) {
    return this.request('/api/admin/stats/daily', { days });
  }

  getDashboard(days = 30) {
    return this.request('/api/admin/analytics/dashboard', { days });
  }

  getRealtime() {
    return this.request('/api/admin/analytics/realtime');
  }

  getEvents(filters = {}) {
    return this.request('/api/admin/analytics/events', {
      event_name: filters.eventName,
      user_id: filters.userId,
      limit: filters.limit || 100,
    });
  }

  getUserAnalytics(userId) {
    return this.request(`/api/admin/analytics/user/${userId}`);
  }

  // ========== Курсы ==========
  getRateSettings() {
    return this.request('/api/admin/rate-settings');
  }

  // ========== Altyn / KYC ==========
  checkKyc(phone) {
    return this.request('/api/admin/altyn/check-kyc', { phone });
  }

  getMerchantPhones() {
    return this.request('/api/admin/merchant/phones');
  }

  // ========== Ордера ==========
  getOrders(status) {
    return this.request('/api/admin/orders', { status });
  }

  getPaidOrders() {
    return this.request('/api/admin/orders/paid');
  }

  getOrder(orderId) {
    return this.request(`/api/admin/order/${orderId}`);
  }

  updateOrderStatus(orderId, status, adminNotes) {
    return this._fetch('PATCH', `/api/order/${orderId}/status`, {
      body: { status, adminNotes },
    });
  }

  deleteOrder(orderId) {
    return this._fetch('DELETE', `/api/admin/order/${orderId}`);
  }

  deleteUserOrders(userId) {
    return this._fetch('DELETE', `/api/admin/users/${userId}/orders`);
  }

  // ========== Действия ==========
  refreshVerification(phone) {
    return this._fetch('POST', '/api/admin/refresh-verification', {
      body: { phone },
    });
  }

  altynPayout(phone, amount, externalId, currency = 'RUB') {
    return this._fetch('POST', '/api/admin/altyn/payout', {
      body: { phone, amount, external_id: externalId, currency },
    });
  }

  // ========== Пользователи ==========
  getUsers() {
    return this.request('/api/admin/users');
  }

  getUserLimits(userId) {
    return this.request(`/api/admin/users/${userId}/limits`);
  }

  getAllLimits() {
    return this.request('/api/admin/limits');
  }

  // ========== Рефералы ==========
  getReferrals(limit = 100) {
    return this.request('/api/admin/referrals', { limit });
  }

  getLeaderboard() {
    return this.request('/api/admin/referrals/leaderboard');
  }

  getReferralCheck(userId) {
    return this.request(`/api/admin/referral/check/${userId}`);
  }

  getReferralEarnings(filters = {}) {
    return this.request('/api/admin/referral/earnings', {
      status: filters.status,
      limit: filters.limit || 100,
    });
  }
}

const api = new AdminAPI();
export default api;
